import 'photoswipe/dist/photoswipe.css'

import styled from '@emotion/styled'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import Button from 'shared/components/Button'
import SlickSlider from 'shared/components/slick-slider'
import VirtualTourDialog from 'shared/components/virtual-tour-dialog'
import React, { useEffect, useState } from 'react'
import { css } from '@emotion/react'
import { capitalize } from 'lodash'
import { Gallery, Item } from 'react-photoswipe-gallery'
import { MoneyInput } from 'shared/components/mask-components'
import { IPictures } from 'shared/models/interfaces/response/realestate.interface'
import { useTranslations } from 'next-intl'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { Skeleton } from '@mui/material'
import FavoritePropertyButton from '@components/property-card/favoriteButton'
import { useAppSelector } from 'custom-hooks/redux.hooks'
import { useMutation } from '@tanstack/react-query'
import { getRealestateDetails } from 'api/realestate.request'
import SharedNetWork from '@components/shared-network'
import { moneyFormatter } from '@components/property-card/withAdapter'

function CustomNextArrow(props: any) {
  return <NextArrow {...props} />;
}

function CustomPrevArrow(props: any) {
  return <PrevArrow {...props} />;
}

function AlbumSection(props: { data: any; isCollection: boolean; id: number }) {
  const { data, isCollection, id } = props;
  const t = useTranslations("PriceRange");

  const user = useAppSelector(({ Auth }) => Auth.user);
  const price = data?.currency.symbol + moneyFormatter.format(data?.price);
  let maximum_price = null;
  let minimum_price = null;
  
  if (data?.project?.minimum_price && data?.project?.maximum_price) {
    maximum_price = `${data.currency.symbol}` + moneyFormatter.format(data.project.maximum_price as any);
    minimum_price = `${data.currency.symbol}` + moneyFormatter.format(data.project.minimum_price as any);
  }

  let cache:any = [];
  const cleanImages = data.pictures.map((picture: any) => {
    if(!cache.includes(picture.pictures)) {
      cache.push(picture.pictures)
      return picture
    }
    return null
  })
  const pictures = cleanImages.filter(Boolean);

  const [mainImage, setMainImage] = useState<IPictures>(pictures[0]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const translation = useTranslations("property");
  const verticalSliderSettings = {
    slidesToShow: 4,
    slidesToScroll: 1,
    vertical: true,
    verticalSwiping: true,
    swipeToSlide: true,
    nextArrow: <CustomNextArrow />,
  };
  const [isFavorite, setIsFavorite] = useState<boolean | null>(
    data.is_favorite
  );
  const { mutate } = useMutation(
    ["propertyDetails", id],
    getRealestateDetails,
    {
      onSuccess: ({ data: { is_favorite } }) => {
        setIsFavorite(!!is_favorite);
      },
    }
  );

  const sliderSettings = {
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    nextArrow: <CustomNextArrow />,
    prevArrow: <CustomPrevArrow />,
  };

  const handleOpen = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const handleMainImageChange = (picture: IPictures) => {
    setMainImage(picture);
  };

  const normalizeString = (value: string) => {
    return capitalize(value.toLowerCase());
  };

  const title = `${normalizeString(data.realstate_type)} en ${normalizeString(
    data.business_type
  )} en ${normalizeString(data.sector)}, ${normalizeString(data.city)}`;

  const addThisProps = {
    title: title,
    description: title,
    picture: mainImage.pictures,
  };

  useEffect(() => {
    handleMainImageChange(pictures[0]);
  }, [data.id]);
  
  const showFavorites = true;

  useEffect(() => {
    if (user) mutate(id);
  }, [user, mutate, id]);

  return (
    <Wrapper>
      <AlbumHeader>
        {isCollection && (
          <AlbumCode isCollection={isCollection}>
            <AlbumCodeText>COLLECTION ® </AlbumCodeText>
            {translation("code")}: {data.id}
          </AlbumCode>
        )}
        {!isCollection && (
          <AlbumCode>
            {translation("code")}: {data.id}
          </AlbumCode>
        )}
        {/* <Tooltip
          content={<ShareTooltip {...addThisProps} />}
          onClose={() => setOpenTooltip(false)}
          open={openTooltip}
          arrow
          placement="bottom-end"
        >
         
        </Tooltip> */}
        <SharedNetWork>
          <AlbumShare>
            <AlbumShareText>{translation("share")}</AlbumShareText>
            <AlbumShareIcon
              src="/icons/icon_share_remaxrd.svg"
              alt="Icono Compartir"
            />
          </AlbumShare>
        </SharedNetWork>
      </AlbumHeader>
      <AlbumBody>
        <Gallery
          withCaption
          withDownloadButton
          options={{
            padding: {
              top: 50,
              bottom: 50,
              left: 0,
              right: 0,
            },
            counter: false,
          }}
        >
          <HorizontalSlider {...sliderSettings}>
            {pictures.map((picture: IPictures, index: number) => (
              <Item
                key={index}
                original={picture.pictures}
                thumbnail={picture.pictures}
                caption={`Image #: ${index + 1}`}
              >
                {({ ref, open }: any) => (
                  <AlbumImage
                    src={picture.pictures}
                    alt={`${data.realstate_type} ${data.sector}`}
                    key={index}
                    delayTime={5000}
                    ref={ref}
                    onClick={open}
                    placeholder={
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        style={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    }
                  />
                )}
              </Item>
            ))}
          </HorizontalSlider>
        </Gallery>

        <Gallery
          withCaption
          withDownloadButton
          options={{
            padding: {
              top: 50,
              bottom: 100,
              left: 0,
              right: 0,
            },
          }}
        >
          {pictures.map((picture: IPictures, index: number) => {
            return (
              <Item
                key={index}
                original={picture.pictures}
                thumbnail={picture.pictures}
                caption={`Image #: ${index + 1}`}
              >
                {({ ref, open }) => (
                  <>
                    <AlbumContentActive
                      onClick={open}
                      ref={ref as any}
                      style={{
                        display:
                          mainImage?.pictures == picture?.pictures
                            ? "block"
                            : "none",
                        cursor: "pointer",
                      }}
                    >
                      <AlbumActive
                        delayTime={5000}
                        src={mainImage?.pictures}
                        alt={`${data.realstate_type} ${data.sector}`}
                        placeholder={
                          <Skeleton
                            variant="rectangular"
                            animation="wave"
                            style={{
                              display: "block",
                              width: "100%",
                              height: "100%",
                            }}
                          />
                        }
                      />
                    </AlbumContentActive>
                  </>
                )}
              </Item>
            );
          })}
        </Gallery>
        <AlbumPreview>
          <VerticalSlider
            {...verticalSliderSettings}
            infinite={pictures.length < 4 ? false : true}
          >
            {pictures.map((picture: IPictures, index: number) => (
              <div key={index} onClick={() => handleMainImageChange(picture)}>
                <AlbumImage
                  src={picture.pictures}
                  alt={`${data.realstate_type} ${data.sector}`}
                  placeholder={
                    <Skeleton
                      variant="rectangular"
                      animation="wave"
                      style={{
                        display: "block",
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  }
                />
              </div>
            ))}
          </VerticalSlider>
        </AlbumPreview>
      </AlbumBody>
      <AlbumFooter>
        {Boolean(data.realstate_tour_url) && (
          <AlbumButton onClick={handleOpen}>
            {translation("matterport")}
          </AlbumButton>
        )}
        <AlbumTag className={data.alternate_price > 0 ? "price-double" : ""}>
          {showFavorites && (
            <AlbumLikes>
              <FavoritePropertyButton
                fontSize="large"
                borderColor="error"
                size={50}
                parent_id={data.parent_id}
                is_favorite={isFavorite}
                detail={data}
              />
            </AlbumLikes>
          )}
          {data.alternate_price > 0 && (
            <>
              <AlbumPrice>
                <AlbumPriceTag className="price-double">VENTA</AlbumPriceTag>
                <AlbumPriceNumber className="price-double">
                  <MoneyInput
                    value={data.price}
                    prefix={data.currency.symbol + "$"}
                    displayType="text"
                  />
                </AlbumPriceNumber>
              </AlbumPrice>

              <AlbumPrice>
                <AlbumPriceTag className="price-double">ALQUILER</AlbumPriceTag>
                <AlbumPriceNumber className="price-double">
                  <MoneyInput
                    value={data.alternate_price}
                    prefix={data.alternative_currency.symbol + "$"}
                    displayType="text"
                  />
                </AlbumPriceNumber>
              </AlbumPrice>
            </>
          )}

          {data.alternate_price <= 0 && (
            <AlbumPrice>
              <AlbumPriceTag>{data.business_type}</AlbumPriceTag>
              <AlbumPriceNumber>
                {maximum_price && minimum_price ? (
                  <>
                    <PriceRangeItem>
                      {t("from")}{" "}
                      <PriceRangeItemPrice>{minimum_price}</PriceRangeItemPrice>
                    </PriceRangeItem>
                    <PriceRangeItem>
                      {t("to")}{" "}
                      <PriceRangeItemPrice>{maximum_price}</PriceRangeItemPrice>
                    </PriceRangeItem>
                  </>
                ) : (
                  price
                )}
              </AlbumPriceNumber>
            </AlbumPrice>
          )}
        </AlbumTag>
      </AlbumFooter>
      {Boolean(data.realstate_tour_url) && (
        <VirtualTourDialog
          open={openDialog}
          onClose={handleClose}
          src={data.realstate_tour_url}
        />
      )}
    </Wrapper>
  );
}

const HorizontalSlider = styled(SlickSlider)`
  display: none;
  height: 100%;
  width: 100%;

  & .slick-list,
  .slick-track,
  .slick-slide > div {
    height: inherit;
  }

  & .slick-list {
    width: inherit;
  }

  @media screen and (max-width: 991px) {
    display: block;
  }
`;

const VerticalSlider = styled(SlickSlider)`
  height: 100%;

  & .slick-list {
    height: 100% !important;
  }

  & .slick-slide {
    border: none;

    & > div {
      line-height: 0;
    }
  }

  & .slick-prev {
    display: none !important;
  }
`;

const NextArrow = styled(ArrowForwardIosIcon)`
  color: var(--color-blue-main);
  top: 100%;
  left: 50%;
  transform: translateX(-50%) rotate(90deg);
  height: 26px;
  width: 26px;

  &:hover {
    color: var(--color-blue-main);
  }

  @media screen and (max-width: 991px) {
    top: 50%;
    transform: rotate(0) translateY(-50%);
    left: calc(100% - 50px);
    color: var(--white);
    height: 50px;
    width: 50px;

    &:hover {
      color: var(--white);
    }
  }
`;

const PrevArrow = styled(ArrowBackIosNewIcon)`
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  color: var(--white);
  height: 50px;
  width: 50px;
  z-index: 1;

  &:hover,
  &:focus {
    color: var(--white);
  }
`;

const Wrapper = styled.section`
  margin: 100px 0 -70px;
  margin-top: 0px;
  width: 100%;

  @media screen and (max-width: 991px) {
    margin-bottom: 30px;
  }
`;

const AlbumHeader = styled.div`
  position: relative;
`;

const AlbumBody = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  height: 50vw;
  max-height: 600px;
  justify-content: space-between;
  align-items: stretch;
  background-color: var(--white);
`;

const AlbumActivePreviewItemSharedCss = css`
  background-size: cover !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
`;

// ${AlbumActivePreviewItemSharedCss}
const AlbumContentActive = styled.div`
  width: 80%;
  height: 50vw;
  max-height: 600px;
  @media screen and (max-width: 1180px) {
    // max-height: 600px;
  }
  @media screen and (max-width: 1054px) {
    // min-height: 70vh;
    // height: 70vh;
  }
  @media screen and (max-width: 991px) {
    display: none;
  }
`;
const AlbumActive = styled(LazyLoadImage)`
  object-fit: cover;
  position: relative;
  width: 100%;
  height: 100%;
  background: white;
  @media screen and (max-width: 991px) {
    display: none;
  }
`;


const SkeletonAlbumActive = styled(Skeleton)`
  object-fit: cover;
  position: relative;
  width: 100%;
  height: 100%;
`; 

const AlbumPreview = styled.div`
  width: calc(20% - 10px);
  height: 100%;

  @media screen and (max-width: 991px) {
    display: none;
  }
`;

const AlbumImage = styled(LazyLoadImage as any)`
  object-fit: cover;
  width: 100%;
  height: 145px;
  margin-bottom: 5px;
  cursor: pointer;
  @media screen and (max-width: 991px) {
    height: inherit;
    margin: 0;
    cursor: auto;
  }
`;

const AlbumFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  @media screen and (max-width: 991px) {
    flex-direction: column;
  }
`;

const AlbumButton = styled(Button)`
  background-color: #969daf;
  width: 169px;
  height: 36px;
  font-size: 1rem;
  font-weight: 400;
  border-radius: 0 0 0 20px;
  text-transform: none;
  line-height: 1.15;
  padding: 0;

  &:hover {
    background-color: #969daf;
    scale: 1;
  }

  @media screen and (max-width: 991px) {
    border-radius: 0 0 20px 20px;
  }
`;

const AlbumTag = styled.div`
  width: fit-content;
  min-height: 106px;
  background-color: var(--white);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  border-radius: 0 0 20px 20px;
  padding: 0 20px;
  &.price-double {
    // width: 578px;
    min-height: 106px;
    padding: 10px 20px;
    gap: 10px 20px;
  }
  @media screen and (max-width: 991px) {
    width: 100%;
    margin-top: 30px;
    border-radius: 20px;
  }
`;

const AlbumLikes = styled.div`
  height: 54px;
  width: 54px;
  padding: 0;
  margin-right: 20px;
  border-right: 1px solid var(--color-blue-main);
`;

const AlbumPrice = styled.p`
  flex: 1;
`;

const AlbumPriceTag = styled.span`
  display: block;
  font-size: ${23 / 16 + "rem"};
  font-weight: 300;
  text-transform: uppercase;
  &.price-double {
    font-size: ${14 / 12 + "rem"};
  }
`;

const AlbumPriceNumber = styled.span`
  font-size: 1.7rem;
  font-weight: 500;
  display: block;
  color: var(--color-blue-main);
  &.price-double {
    font-size: ${16 / 12 + "rem"};
  }
`;

const AlbumCodeShareCss = css`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const AlbumCode = styled.p<AlbumCodeProps>`
  ${AlbumCodeShareCss};
  background-color: #1f3564;
  text-align: center;
  padding: 17px 30px 12px;
  color: var(--white);
  font-weight: 500;
  font-size: ${17 / 16 + "rem"};
  text-decoration: none;
  border-radius: 20px 20px 0 0;
  height: 47px;
  display: inline-block;
  width: auto;
  margin-top: 40px;

  ${(props) => {
    if (props.isCollection) {
      return css`
        background-color: #ec1c25;
      `;
    }
  }}
`;

const AlbumCodeText = styled.span`
  color: var(--white);

  @media screen and (max-width: 570px) {
    display: none;
  }
`;

const AlbumShare = styled.p`
  ${AlbumCodeShareCss};
  background-color: var(--white);
  border-radius: 20px 20px 0 0;
  font-weight: 500;
  font-size: ${14 / 16 + "rem"};
  position: absolute;
  right: 0;
  bottom: 0;
  width: 190px;
  height: 45px;
  cursor: pointer;

  &:hover {
    color: var(--color-gray-01);
  }

  @media screen and (max-width: 991px) {
    width: 80px;
    height: 50px;
  }
`;

const AlbumShareText = styled.span`
  color: #1f3564;
  padding-top: 5px;

  @media screen and (max-width: 991px) {
    display: none;
  }
`;

const AlbumShareIcon = styled.img`
  margin-left: 5px;
  vertical-align: middle;
  border-style: none;
  @media screen and (max-width: 991px) {
    margin-left: 0;
  }
`;

interface AlbumCodeProps {
  isCollection?: boolean;
}

const PriceRangeItem = styled.p`
  font-weight: 500;
  font-size: 16px;
  margin-bottom: 2px;
  text-transform: uppercase;

  @media (max-width: 500px) {
    font-size: 15px;
  }
`;

const PriceRangeItemPrice = styled.span`
  font-weight: 700;
  font-size: 1.4rem;

  @media (max-width: 500px) {
    font-size: 1.2rem;
  }
`;

export default AlbumSection;
